<template>
  <div>
    <div class="text-center">
      <span class="text-19px leading-24px font-semibold">
        {{ _t('package.share') }}
      </span>

      <Icon
        name="close-circle"
        class="cursor-pointer absolute right-4 top-3 w-22px"
        @click="emit('done')"
      ></Icon>
    </div>

    <label
      data-required
      class="block font-semibold text-15px mt-3 text-ld-label-secondary leading-none"
    >
      {{ _t('package.form_tag') }}
    </label>

    <AutoComplete
      class="w-full mt-2"
      :model-value="tagValue"
      :suggestions="filteredContentTags"
      dropdown
      optionLabel="name"
      optionGroupLabel="name"
      optionGroupChildren="children"
      :placeholder="_t('package.form_tip2')"
      :emptySearchMessage="_t('package.form_tip3')"
      :pt="{
        input: { value: inputDisplay, id: 'tags-input' },
        panel: {
          class: 'content-tags-panel',
          style: `z-index: ${zindexMgmt.nextDialogZIndex()}`,
        },
      }"
      @complete="searchTag"
      @update:model-value="onTagChange"
      @dropdown-click="onDropdownClick"
    ></AutoComplete>

    <label
      class="block font-semibold text-15px mt-3 text-ld-label-secondary leading-none"
    >
      {{ _t('package.form_desc') }}
    </label>
    <Textarea
      v-model="description"
      class="w-full mt-2"
      rows="4"
      autoResize
      maxlength="1000"
    />

    <template v-if="isShareOpen">
      <div class="text-15x leading-none text-ld-brand-500 font-semibold mt-3">
        {{ _t('package.share_tip') }}
      </div>

      <div class="w-full mt-2 flex h-38px text-ld-label-secondary">
        <div
          class="flex-1 px-2 border-solid border-1 border-r-none rounded-l-lg flex items-center text-14px line-clamp-1"
        >
          <Icon
            name="globe"
            class="w-16px h-16px mr-2 shrink-0"
          ></Icon>
          {{ shareLink }}
        </div>
        <div
          class="px-4 cursor-pointer text-14px bg-ld-background border-solid border-1 rounded-r-lg flex items-center"
          @click="onLinkCopy"
        >
          {{ _t('package.share_link_copy') }}
        </div>
      </div>
    </template>

    <div class="mt-6">
      <div
        v-if="isShareOpen"
        class="flex gap-3"
      >
        <Button
          :label="_t('package.share_off')"
          :loading="loading"
          scene="danger"
          class="flex-1"
          @click="onShareClose()"
        />
        <Button
          :label="_t('package.publish_change')"
          class="flex-1"
          @click="onPublish()"
        />
      </div>

      <div v-else>
        <div class="text-17px leading-[1.2] font-semibold">
          {{ _t('package.share_tip2') }}
        </div>
        <Button
          class="w-full mt-2"
          :label="_t('package.share_on')"
          :loading="loading"
          @click="onSharePublish()"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  fetchContentTags,
  fetchFrequentlyContentTags,
  fetchPackageSnapshot,
  ShareStatus,
  updatePackageShare,
} from '@/api/package-source'
import { computed } from 'vue'
import { ref } from 'vue'
import PublishInfo from './PublishInfo.vue'

import type { Package, PackageBasic } from '@/api/package-source'
import Textarea from 'primevue/textarea'
import AutoComplete from 'primevue/autocomplete'
import { tree2Map } from '@/utils/tag'
import type { ContentTag } from '@/types/core'
import type { AutoCompleteCompleteEvent } from 'primevue/autocomplete'
import { useCopy } from '@/hooks'
import zindexMgmt from '@/utils/zindexMgmt'

interface ContentTagOption extends ContentTag {
  parent: ContentTag
  children?: ContentTagOption[]
}

const props = defineProps<{
  package: PackageBasic
}>()

const emit = defineEmits<{
  done: []
  update: [PackageBasic]
}>()

const pkgPublicPath = computed(() => `/pkgs/${props.package.hashId}`)

const shareLink = computed(
  () => `${location.protocol}//${location.host}${pkgPublicPath.value}`
)

const copy = useCopy()

const isShareOpen = computed(() => shareStatus.value === ShareStatus.COMMUNITY)
const shareStatus = ref(props.package.owned?.shareStatus)

const loading = ref(false)

const loadingPkgSnapshot = ref(true)

// 快照
const packageSnapshot = ref<Package>()

// 简介
const description = ref(props.package.description)

let cacheMap = new Map<string, ContentTagOption>()
const selectedTagKey = ref<string | undefined>(
  props.package?.contentTags[0]?.tag.key
)

const tagValue = ref<string | ContentTagOption | undefined>(
  selectedTagKey.value
)
const contentTags = ref<ContentTagOption[]>([])
const filteredContentTags = ref<ContentTagOption[]>([])

const selectedTag = computed(() => {
  if (selectedTagKey.value == null) return undefined

  return cacheMap.get(selectedTagKey.value)
})

const inputDisplay = computed(() => {
  if (typeof tagValue.value === 'string') {
    return tagValue.value
  } else if (selectedTag.value) {
    const uniqueName = selectedTag.value.uniqueName

    return uniqueName != null
      ? selectedTag.value.uniqueName
      : selectedTag.value.name
  }

  return null
})

function getContentTagLeafs(tag: ContentTag, leafs: ContentTagOption[]) {
  if (!tag.children) return leafs

  for (const item of tag.children) {
    if (!item.children) {
      leafs.push({
        ...item,
        parent: tag,
        children: [],
      })
    } else {
      getContentTagLeafs(item, leafs)
    }
  }

  return leafs
}

function onTagChange(opt: ContentTagOption) {
  tagValue.value = opt

  // 仅当选择选项时，设置 selectedTagKey
  if (opt.key) {
    selectedTagKey.value = opt.key
  }
}

function onDropdownClick() {
  const input = document.querySelector('#tags-input') as HTMLInputElement

  // 点击展开按钮时组件默认会聚焦到输入框导致看不见下拉菜单，这里自动取消聚焦
  if (input) {
    setTimeout(() => {
      input.blur()
    })
  }
}

function searchTag(evt: AutoCompleteCompleteEvent) {
  const filteredOptions: ContentTagOption[] = []
  const query = evt.query.trim()

  if (!query) {
    filteredContentTags.value = [...contentTags.value]
    return
  }

  for (const parent of contentTags.value) {
    const filteredChildren = parent.children!.filter(item => {
      return item.name.includes(query)
    })
    const matched = parent.name.includes(query)

    // 当父节点匹配的时候，无需再筛选子节点
    if (matched) {
      filteredOptions.push(parent)
    } else if (filteredChildren.length > 0) {
      filteredOptions.push({
        ...parent,
        children: filteredChildren,
      })
    }
  }

  filteredContentTags.value = filteredOptions
}

onInit(() => {
  loadingPkgSnapshot.value = true
  Promise.all([
    fetchContentTags('main'),
    fetchFrequentlyContentTags('main'),
    fetchPackageSnapshot(props.package.id),
  ]).then(([{ tree }, { contentTags: frequentlyContentTags }, { pkg }]) => {
    packageSnapshot.value = pkg

    const allLeafs = getContentTagLeafs(tree, [])
    const parents: Record<string, ContentTagOption> = {}

    for (const leaf of allLeafs) {
      const parent = leaf.parent

      if (parents[parent.key]) {
        parents[parent.key].children?.push(leaf)
      } else {
        parents[parent.key] = {
          ...parent,
          parent,
          children: [leaf],
        }
      }
    }

    const frequentlyGroup: ContentTagOption = {
      key: 'frequently',
      name: _t('package.frequently_tag'),
      hidden: false,
      parent: tree,
      aliases: [],
      children: [],
    }

    frequentlyGroup.children = frequentlyContentTags.map(item => {
      return {
        hidden: false,
        aliases: [],
        name: item.name,
        key: item.key,
        parent: frequentlyGroup,
        children: [],
      }
    })

    const options = [frequentlyGroup, ...Object.values(parents)]

    contentTags.value = options
    filteredContentTags.value = options
    cacheMap = tree2Map(options)

    tagValue.value = selectedTag.value
    loadingPkgSnapshot.value = false
  })
})

async function onSharePublish() {
  // 检查是否选择了标签
  if (!selectedTagKey.value) {
    _message.info('请填写分类')
    return
  }

  loading.value = true
  try {
    const res = await updatePackageShare(
      props.package.id,
      ShareStatus.COMMUNITY,
      [
        {
          key: selectedTagKey.value!,
          rootKey: 'main',
        },
      ],
      description.value
    )

    if (res.code !== 0) {
      _message.info(res.message)
      return
    } else {
      _message.info('已开启共享')
      shareStatus.value = res.data.owned?.shareStatus

      emit('update', res.data)
    }
  } finally {
    loading.value = false
  }
}

function onLinkCopy() {
  copy(shareLink.value).then(() => {
    _message.info('链接已复制')
  })
}

async function onPublish() {
  if (!selectedTagKey.value) {
    _message.info('请填写分类')
    return
  }

  _openDialog(PublishInfo, {
    title: _t('package.publish_change'),
    props: {
      contentTags: [
        {
          key: selectedTag.value!.key,
          name: selectedTag.value!.name,
          rootKey: 'main',
        },
      ],
      description: description.value,
      packageId: props.package.id,
      onRelease(newPkg: PackageBasic) {
        emit('update', newPkg)
      },
    },
  })
}

async function onShareClose() {
  loading.value = true
  try {
    const res = await updatePackageShare(props.package.id, ShareStatus.OFF)

    if (res.code !== 0) {
      _message.info(res.message)
    } else {
      _message.info('已关闭共享')

      emit('update', res.data)
      emit('done')
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped></style>
